import type { FetchArgs } from '@reduxjs/toolkit/query/react';

import { apiSlice } from '@/store/apiSlice';
import type { PolicyEvaluateRequest, PolicyEvaluateResult } from './model';

/**
 * `POST /policies/evaluate/self` — the one endpoint behind `can()` (design §13.2
 * `policyApi`). A **mutation** on purpose: the permission cache below owns
 * freshness/TTL, so RTK Query must never auto-catch or auto-garbage-collect a
 * decision (and its tag invalidation is not what expires entries here).
 *
 * Route note: `/policies/evaluate` is `@Public()` + backplane-secret guarded
 * (service-to-service only) and fails closed in production, so the browser —
 * which holds a user JWT, never the shared secret — calls the additive
 * JWT-authenticated variant `/policies/evaluate/self`. The server binds the
 * subject to `req.user.id` and ignores the `userId` we still send (the shared
 * strict schema requires it), so role-gating is real in prod while keeping
 * fail-closed semantics client-side.
 *
 * Path note (design Q7): the authorization service mounts `PolicyController` at
 * `policies` with **no** global prefix, and this app's `/api/authorization`
 * proxy does *not* rewrite, so `/api/authorization/...` would 404. The working
 * prefix is the admin-ui one: `/api/policies/...` (rewritten to
 * `/policies/...`). Same origin/proxy surface.
 */
export const policyApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    evaluatePolicy: builder.mutation<PolicyEvaluateResult, PolicyEvaluateRequest>({
      query: (body): FetchArgs => ({
        url: 'policies/evaluate/self',
        method: 'POST',
        body,
      }),
      // A denied decision is data, not an error; the permission cache owns TTL,
      // de-duplication and invalidation (see `permissionCache.ts`).
    }),
  }),
});

export const { useEvaluatePolicyMutation } = policyApi;
